import { format } from 'date-fns';
import { CheckCircle, Redo2, ChevronRight, Check } from 'lucide-react';
import type { UserProgram } from '../../types/userProgram.interface';
import type { ProgramInterface } from '../../types/program.interface';

interface Props {
  userProgram: UserProgram
  program: ProgramInterface
  total: number
  showSignUpModal: () => void
  goToVideos: () => void
}

export const ProgramProgress = ({ userProgram, program, total, showSignUpModal, goToVideos }: Props) => {
  const programStart = new Date(userProgram.programStart);

  let previousWeekNotCompleted = false;
  const programProgress = (program?.weeks || []).map(week => {
    const reduced = week.every(video =>
      userProgram.completed.some(userVideo => userVideo.video === video.video._id)
    );
    previousWeekNotCompleted = previousWeekNotCompleted || !reduced;
    return !previousWeekNotCompleted;
  });

  const programLength = program?.weeks?.length || 0;
  let currentWeekCompleteIndex = programProgress.lastIndexOf(true);
  let currentWeekNotCompleteIndex = programProgress.indexOf(false);

  if (currentWeekCompleteIndex === -1) {
    programProgress[0] = true;
  } else {
    if (currentWeekCompleteIndex + 1 >= programLength) {
      currentWeekNotCompleteIndex = programLength;
    } else {
      currentWeekNotCompleteIndex += 1;
      programProgress[currentWeekCompleteIndex + 1] = true;
    }
  }

  const currentWeek = currentWeekCompleteIndex === -1 ? 1 : currentWeekNotCompleteIndex;
  const currentWeekSchedule = (program?.weeks || [])[currentWeek - 1] || [];
  const currentWeekProgress = currentWeekSchedule.filter(video =>
    userProgram.completed.some(userVideo => userVideo.video === video.video._id)
  ).length;

  return (
    <div className="space-y-6 mt-15 bg-white p-6 rounded-md shadow-md">
      {userProgram.programCompleted ? (
        <div className="flex justify-end">
          <div className="text-center">
            <CheckCircle className="text-green-500 w-6 h-6 mx-auto mb-1" />
            <div className="text-sm text-gray-800">
              Completaste este programa en: {format(new Date(userProgram.programCompleted), 'dd/MM/yyyy hh:mma')}
            </div>
            <button onClick={showSignUpModal} className="cursor-pointer text-holos-dark text-xs mt-1 flex items-center justify-center gap-1">
              <Redo2 className="w-3 h-3" /> Reiniciar programa
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-lg font-semibold">Tu Progreso</h2>
            <div>
              <p className="text-xs text-right font-medium">
                Semana {Math.min(currentWeek, programLength)} de {programLength}
              </p>
              <div className="flex space-x-2 mt-1">
                {programProgress.map((active, i) => (
                  <span
                    key={i}
                    className={`h-2 rounded-full ${active ? 'bg-holos-dark' : 'bg-gray-300'}`}
                    style={{ width: `${100 / programLength}%` }}
                  ></span>
                ))}
              </div>
              <p className="text-right text-xs text-gray-500 mt-1">
                Fecha de inicio: {format(programStart, 'dd/MM/yyyy')}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-around items-center border rounded-lg py-4 text-center">
        <div className="flex flex-col justify-center">
          <img className="mb-2 h-10" src='https://cache.beholos.com/img/fitness-pass-v2/global/calendar-icon.svg' />
          <p className="text-lg font-light">{currentWeekProgress}/{currentWeekSchedule.length}</p>
          <p className="text-xs uppercase text-gray-500">Videos esta semana</p>
        </div>
        <div className="flex flex-col justify-center">
          <img className="mb-2 h-10" src='https://cache.beholos.com/img/fitness-pass-v2/global/clock-icon.svg' />
          <p className="text-lg font-light">{Math.round(userProgram.completed.length / currentWeek)}</p>
          <p className="text-xs uppercase text-gray-500">Promedio por semana</p>
        </div>
        <div className="flex flex-col justify-center">
          <img className="mb-2 h-10" src='https://cache.beholos.com/img/fitness-pass-v2/global/dumbell-icon.svg' />
          <p className="text-lg font-light">{total}</p>
          <p className="text-xs uppercase text-gray-500">Videos totales</p>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Esta Semana</h2>
        <a onClick={goToVideos} className="text-sm text-holos-dark flex items-center gap-1 cursor-pointer">
          Ver Todos <ChevronRight className="w-4 h-4" />
        </a>
      </div>

      <div className="space-y-3">
        {currentWeekSchedule.map((item, index) => {
          const { video } = item;
          const completed = userProgram.completed.some(v => v.video === video._id);
          return (
            <a
              key={video._id}
              href={`/user/clases-online/video/${video.slug}`}
              className="flex items-center border-t pt-3 last:border-b pb-3"
            >
              <div className="flex-grow">
                <p className="text-sm font-medium">
                  {program.dayCount && <span className="text-holos-dark">Día {index + 1}: </span>}
                  {video.title}
                </p>
                <p className="text-xs text-gray-600 hidden md:block">
                  {(video.description || '').slice(0, 250)}
                </p>
              </div>
              <p className="text-xs text-gray-400 px-4 hidden md:block">{video.duration} min.</p>
              <div
                className={`w-7 h-7 aspect-square shrink-0 rounded-full border box-border flex items-center justify-center mx-2 
                  ${completed ? 'bg-holos-dark text-white' : 'bg-white border-gray-300 text-transparent'}
                `}>
                {completed && <Check className="w-4 h-4" />}
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </a>
          );
        })}
      </div>
    </div>
  );
}
